/* Central UK property purchase tax configuration + calculator.
   ALL rates, bands, thresholds and effective dates live HERE — update this one
   file after a Budget or rate change. Sources verified 5 August 2026:
   - England & NI (SDLT): https://www.gov.uk/stamp-duty-land-tax/residential-property-rates
   - Wales (LTT):         https://www.gov.wales/land-transaction-tax-rates-and-bands
   - Scotland (LBTT):     https://revenue.scot/taxes/land-buildings-transaction-tax/residential-property
*/

export const RATES_LAST_REVIEWED = "5 August 2026";

const SDLT = {
  taxName: "Stamp Duty Land Tax",
  shortName: "SDLT",
  source: { name: "GOV.UK", url: "https://www.gov.uk/stamp-duty-land-tax/residential-property-rates" },
  effectiveFrom: "1 April 2025",
  standardBands: [
    { upTo: 125000, rate: 0 },
    { upTo: 250000, rate: 2 },
    { upTo: 925000, rate: 5 },
    { upTo: 1500000, rate: 10 },
    { upTo: null, rate: 12 },
  ],
  ftbRelief: {
    maxPrice: 500000,
    bands: [
      { upTo: 300000, rate: 0 },
      { upTo: 500000, rate: 5 },
    ],
  },
  additional: { type: "band-surcharge", points: 5, minPrice: 40000 },
  nonResidentSurchargePoints: 2,
  hasResidencyQuestion: true,
  hasPreviousHomeQuestion: true,
};

export const TAX_CONFIG = {
  england: SDLT,
  "northern-ireland": SDLT,
  wales: {
    taxName: "Land Transaction Tax",
    shortName: "LTT",
    source: { name: "Welsh Government / Welsh Revenue Authority", url: "https://www.gov.wales/land-transaction-tax-rates-and-bands" },
    effectiveFrom: "main rates 10 October 2022; higher rates 11 December 2024",
    standardBands: [
      { upTo: 225000, rate: 0 },
      { upTo: 400000, rate: 6 },
      { upTo: 750000, rate: 7.5 },
      { upTo: 1500000, rate: 10 },
      { upTo: null, rate: 12 },
    ],
    ftbRelief: null,
    additional: {
      type: "replacement-bands",
      minPrice: 40000,
      bands: [
        { upTo: 180000, rate: 5 },
        { upTo: 250000, rate: 8.5 },
        { upTo: 400000, rate: 10 },
        { upTo: 750000, rate: 12.5 },
        { upTo: 1500000, rate: 15 },
        { upTo: null, rate: 17 },
      ],
    },
    nonResidentSurchargePoints: null,
    hasResidencyQuestion: false,
    hasPreviousHomeQuestion: false,
  },
  scotland: {
    taxName: "Land and Buildings Transaction Tax",
    shortName: "LBTT",
    source: { name: "Revenue Scotland", url: "https://revenue.scot/taxes/land-buildings-transaction-tax/residential-property" },
    effectiveFrom: "standard rates 1 April 2021; ADS rate 5 December 2024",
    standardBands: [
      { upTo: 145000, rate: 0 },
      { upTo: 250000, rate: 2 },
      { upTo: 325000, rate: 5 },
      { upTo: 750000, rate: 10 },
      { upTo: null, rate: 12 },
    ],
    ftbRelief: {
      maxPrice: null,
      bands: [
        { upTo: 175000, rate: 0 },
        { upTo: 250000, rate: 2 },
        { upTo: 325000, rate: 5 },
        { upTo: 750000, rate: 10 },
        { upTo: null, rate: 12 },
      ],
    },
    additional: { type: "flat-supplement", rate: 8, minPrice: 40000, name: "Additional Dwelling Supplement (ADS)" },
    nonResidentSurchargePoints: null,
    hasResidencyQuestion: false,
    hasPreviousHomeQuestion: false,
  },
};

export const JURISDICTIONS = [
  { id: "england", label: "England" },
  { id: "northern-ireland", label: "Northern Ireland" },
  { id: "wales", label: "Wales" },
  { id: "scotland", label: "Scotland" },
];

const fmt = (n) => `£${n.toLocaleString("en-GB")}`;

const bandBreakdown = (price, bands, extraPoints = 0) => {
  const rows = [];
  let prev = 0;
  let total = 0;
  for (const b of bands) {
    const cap = b.upTo == null ? Infinity : b.upTo;
    if (price <= prev) break;
    const portion = Math.min(price, cap) - prev;
    const rate = b.rate + extraPoints;
    const tax = (portion * rate) / 100;
    rows.push({ from: prev, to: b.upTo, rate, tax });
    total += tax;
    prev = cap;
    if (price <= cap) break;
  }
  return { rows, total };
};

/* buyerType: "first_time_buyer" | "home_mover" | "additional_property"
   residency: "yes" | "no" | "unsure"  (England / Northern Ireland only)
   previousHomeSold: "yes" | "no" | "unsure"  (England / NI, home_mover only)

   England & NI mover rule: if the previous main residence will still be owned on
   the completion date, the higher rates for additional properties apply. A refund
   may later be available if the former home is sold within the relevant window.
*/
export const calculatePropertyTax = ({ jurisdiction, price, buyerType, residency, previousHomeSold }) => {
  const cfg = TAX_CONFIG[jurisdiction];
  if (!cfg || !price || price <= 0) return null;

  const notes = [];
  let bands = cfg.standardBands;
  let extraPoints = 0;
  let scheduleLabel = "Standard residential rates";
  let supplement = null;
  let reliefApplied = false;
  let higherRatesApplied = false;
  let higherRatesReason = null; // "additional" | "not-replacing"

  // England & NI: mover who hasn't sold their previous main home triggers higher rates.
  const treatAsAdditional =
    buyerType === "additional_property" ||
    (buyerType === "home_mover" && cfg.hasPreviousHomeQuestion && previousHomeSold === "no");

  if (treatAsAdditional) {
    const a = cfg.additional;
    if (price < a.minPrice) {
      notes.push(`Purchases under ${fmt(a.minPrice)} are not subject to the higher rates for additional properties — standard rates apply.`);
    } else if (a.type === "band-surcharge") {
      extraPoints += a.points;
      higherRatesApplied = true;
      higherRatesReason = buyerType === "additional_property" ? "additional" : "not-replacing";
      scheduleLabel = `Higher rates for additional properties (standard rates + ${a.points} percentage points)`;
    } else if (a.type === "replacement-bands") {
      bands = a.bands;
      higherRatesApplied = true;
      higherRatesReason = "additional";
      scheduleLabel = "Higher residential rates for additional properties";
    } else if (a.type === "flat-supplement") {
      supplement = { name: a.name, rate: a.rate, amount: (price * a.rate) / 100 };
      higherRatesApplied = true;
      higherRatesReason = "additional";
      scheduleLabel = `Standard rates plus ${a.name} at ${a.rate}%`;
    }
  } else if (buyerType === "first_time_buyer") {
    if (!cfg.ftbRelief) {
      notes.push("Wales does not offer a separate first-time buyer relief — the main residential rates apply to everyone.");
    } else if (cfg.ftbRelief.maxPrice && price > cfg.ftbRelief.maxPrice) {
      notes.push(`First-time buyer relief is not available above ${fmt(cfg.ftbRelief.maxPrice)} — standard rates apply to the whole price.`);
    } else {
      bands = cfg.ftbRelief.bands;
      reliefApplied = true;
      scheduleLabel = "First-time buyer relief rates";
    }
  }

  let nonResidentApplied = false;
  if (cfg.hasResidencyQuestion && residency === "no") {
    extraPoints += cfg.nonResidentSurchargePoints;
    nonResidentApplied = true;
  }

  const { rows, total: bandTax } = bandBreakdown(price, bands, extraPoints);
  const total = bandTax + (supplement ? supplement.amount : 0);

  return {
    taxName: cfg.taxName,
    shortName: cfg.shortName,
    source: cfg.source,
    effectiveFrom: cfg.effectiveFrom,
    total,
    effectiveRate: (total / price) * 100,
    rows,
    supplement,
    notes,
    reliefApplied,
    higherRatesApplied,
    higherRatesReason,
    nonResidentApplied,
    residencyUnsure: cfg.hasResidencyQuestion && residency === "unsure",
    previousHomeUnsure: cfg.hasPreviousHomeQuestion && buyerType === "home_mover" && previousHomeSold === "unsure",
    scheduleLabel,
  };
};

export const priceBand = (price) => {
  if (price < 250000) return "under_250k";
  if (price < 500000) return "250k_500k";
  if (price < 1000000) return "500k_1m";
  return "1m_plus";
};
